import type {UserPropertyADto} from '@cohort/admin-schemas/userProperty';
import type {
  BrevoContactAttribute,
  MappedUserProperty,
} from '@cohort/merchants/apps/brevo/sync/utils';
import {getDataTypeFromBrevoAttributeType} from '@cohort/merchants/apps/brevo/sync/utils';
import DataTypeIcon from '@cohort/merchants/components/DataTypeIcon';
import {SelectPicker} from '@cohort/merchants/components/form/select/SelectPicker';
import {useCohortForm} from '@cohort/merchants/hooks/contexts/form';
import type {SyncConfigFormValues} from '@cohort/merchants/pages/apps/app/utils';
import {useFieldArray} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

interface ExportUsersAttributeFieldSelectProps {
  index: number;
  brevoContactAttributes: BrevoContactAttribute[];
  userProperties: UserPropertyADto[] | undefined;
  mappedUserProperty: MappedUserProperty;
}

const ExportUsersAttributeFieldSelect: React.FC<ExportUsersAttributeFieldSelectProps> = ({
  index,
  brevoContactAttributes,
  mappedUserProperty,
}) => {
  const {t} = useTranslation('app-brevo', {
    keyPrefix: 'sync.exportUsersAttributeFieldSelect',
  });

  const {control, watch} = useCohortForm<Extract<SyncConfigFormValues, {appId: 'brevo'}>>();

  const isExportEnabled = watch('userExportEnabled');
  const mappedUserProperties = watch('userExportConfig.userProperties');

  const {replace: replaceUserProperties} = useFieldArray({
    control,
    name: 'userExportConfig.userProperties',
  });

  const selectedBrevoAttributeNames = mappedUserProperties.map(
    mappedUserProperty => mappedUserProperty.brevoAttributeFieldName
  );
  const brevoAttributeOptions = brevoContactAttributes
    .filter(
      attr =>
        getDataTypeFromBrevoAttributeType(attr.type) === mappedUserProperty.userProperty?.dataType
    )
    .map(attr => ({
      label: attr.name,
      value: attr.name,
      isDisabled: selectedBrevoAttributeNames.includes(attr.name),
    }));

  const brevoAttributeValue =
    mappedUserProperty.brevoAttribute !== null
      ? {
          value: mappedUserProperty.brevoAttribute.name,
          label: mappedUserProperty.brevoAttribute.name,
        }
      : null;

  const handleChange = (index: number, attribute: BrevoContactAttribute | null): void => {
    const updatedMappedUserProperties = [...mappedUserProperties];

    if (updatedMappedUserProperties[index] !== undefined && attribute !== null) {
      updatedMappedUserProperties[index] = {
        ...updatedMappedUserProperties[index],
        brevoAttributeFieldName: attribute.name,
      };
      replaceUserProperties(updatedMappedUserProperties);
    }
  };

  return (
    <SelectPicker
      name={`attribute-${index}`}
      placeholder={t('placeholder')}
      formatOptionLabel={data => {
        const attributeType = brevoContactAttributes.find(attr => attr.name === data.value)?.type;
        return (
          <div className="flex items-center gap-2">
            {attributeType && (
              <DataTypeIcon dataType={getDataTypeFromBrevoAttributeType(attributeType)} />
            )}
            <span>{data.label}</span>
          </div>
        );
      }}
      options={brevoAttributeOptions}
      value={brevoAttributeValue}
      isDisabled={!isExportEnabled || mappedUserProperty.userProperty === null}
      onChange={option => {
        handleChange(
          index,
          brevoContactAttributes.find(attr => attr.name === option?.value) ?? null
        );
      }}
    />
  );
};

export default ExportUsersAttributeFieldSelect;
